import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@mui/material';

import UploadIcon from '@mui/icons-material/CloudUploadOutlined';

import clsx from 'clsx';

import { useFileList } from '../../../hooks/use-file-list';
import theme from '../../../styles/theme';
import { IAppFile } from '../../../types/interfaces/app-file';
import { INewFile } from '../../../types/interfaces/new-file';
import AppDocsViewer from '../app-docs-viewer/AppDocsViewer';
import AppFile from '../app-file/AppFile';
import { useStyles } from './AppFileContainer.styles';

interface AppFilesProps {
  files: IAppFile[];
  onFilesToDeleteChanged?: (ids: string[]) => void;
  onFilesToUploadChanged?: (files: INewFile[]) => void;
  onMainFileChanged?: (id: string | number) => void;
  editMode?: boolean;
  mainFileLabel?: string;
  mainFileId?: string | number;
  acceptedMimeTypes?: string;
  canSetAsMainFile?: (file: IAppFile | INewFile) => boolean;
}

export default function AppFileContainer(props: AppFilesProps) {
  const { classes } = useStyles(theme);
  const { t } = useTranslation();

  const {
    files,
    editMode = false,
    onFilesToDeleteChanged,
    onFilesToUploadChanged,
    onMainFileChanged,
    mainFileLabel = t('common.avatar'),
    mainFileId,
    acceptedMimeTypes = 'image/png, image/jpeg, image/gif',
    canSetAsMainFile = () => true,
  } = props;

  const [selectedFile, setSelectedFile] = useState(null);
  const onFilesToDeleteChangedRef = useRef(onFilesToDeleteChanged);
  const onFilesToUploadChangedRef = useRef(onFilesToUploadChanged);
  const onMainFileChangedRef = useRef(onMainFileChanged);
  const canSetAsMainFileRef = useRef(canSetAsMainFile);

  useEffect(() => {
    onFilesToDeleteChangedRef.current = onFilesToDeleteChanged;
  }, [onFilesToDeleteChanged]);

  useEffect(() => {
    onFilesToUploadChangedRef.current = onFilesToUploadChanged;
  }, [onFilesToUploadChanged]);

  useEffect(() => {
    onMainFileChangedRef.current = onMainFileChanged;
  }, [onMainFileChanged]);

  useEffect(() => {
    canSetAsMainFileRef.current = canSetAsMainFile;
  }, [canSetAsMainFile]);

  const {
    fileIdsToDelete,
    filesToUpload,
    resultFiles,
    onDeleteFile,
    onUploadFiles,
    resetFilesState,
  } = useFileList(files || []);

  useEffect(() => {
    onFilesToDeleteChangedRef.current?.(fileIdsToDelete);
  }, [fileIdsToDelete]);

  useEffect(() => {
    onFilesToUploadChangedRef.current?.(filesToUpload);

    if (filesToUpload?.length && !mainFileId) {
      const firstSelectableFile = filesToUpload.find((file) =>
        canSetAsMainFileRef.current?.(file),
      );
      if (firstSelectableFile) {
        onMainFileChangedRef.current?.(firstSelectableFile.id);
      }
    }
  }, [filesToUpload, mainFileId]);

  return (
    <Box className={classes.filesContainerPageWrapper}>
      <Box className={classes.filesWrapper}>
        {editMode && (
          <Box component="label" className={classes.uploadFileLabel}>
            <input
              autoComplete="off"
              multiple
              style={{ display: 'none' }}
              tabIndex={-1}
              type="file"
              accept={acceptedMimeTypes}
              onChange={(e) => onUploadFiles(e.target.files, false)}
            />
            <UploadIcon />
          </Box>
        )}
        {resultFiles?.map((file, index) => {
          const isMain =
            mainFileId != null && String(file.id) === String(mainFileId);
          return (
            <Box
              key={file.id}
              className={clsx(classes.file, isMain && classes.mainFile)}>
              <AppFile
                key={file?.id}
                file={file}
                onDelete={
                  editMode &&
                  ((file: IAppFile | INewFile) => onDeleteFile(file.id))
                }
                onClick={() => {
                  if (editMode && onMainFileChanged && canSetAsMainFile(file)) {
                    onMainFileChanged(file?.id);
                  }

                  if (!editMode) {
                    setSelectedFile(file);
                  }
                }}
              />
              {isMain && (
                <Box className={classes.mainFileLabel}>{mainFileLabel}</Box>
              )}
            </Box>
          );
        })}
      </Box>

      {!editMode && selectedFile && (
        <AppDocsViewer
          files={resultFiles}
          selectedFile={selectedFile}
          setSelectedFile={setSelectedFile}
        />
      )}
    </Box>
  );
}
